import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAppStore } from '../store/useAppStore';
import { useOnboardingStore } from '../store/useOnboardingStore';
import { useUserDetails, useUserIdentity } from '../queries/user';
import { useCheckProfile } from '../queries/profile';
import { api } from '../services/api';
import { extractAccessToken, clearAuthData } from '../utils/authUtils';
import NoAuthPage from '../pages/NoAuthPage';
import { getRedirectUrl } from '../utils/helpers';

const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  const {
    isAuthenticated,
    setAccessKey,
    setUser,
    setOnboardingStep,
    onboardingStep,
    isLoggingOut,
    isCheckingAuth,
    setIsCheckingAuth,
  } = useAppStore();

  const { userEmail, firstTimeLogin, setUserEmail, setFirstTimeLogin, resetOnboarding } =
    useOnboardingStore();

  const [authFailed, setAuthFailed] = useState(false);
  const [accessKey, setAccessKeyState] = useState(null);
  const [shouldFetchIdentity, setShouldFetchIdentity] = useState(false);

  useEffect(() => {
    if (isLoggingOut && !isCheckingAuth) {
      clearAuthData();
      window.location.href = getRedirectUrl('/login');
      return;
    }

    // Start checking auth on mount or when logging out changes
    setIsCheckingAuth(true);
    setAuthFailed(false);
    const key = extractAccessToken();
    if (!key) {
      setAuthFailed(true);
      setIsCheckingAuth(false);
      setAccessKey('');
      api.setAccessKey('');
      return;
    }
    api.setAccessKey(key);
    setAccessKey(key);
    setAccessKeyState(key);
  }, [isLoggingOut, setAccessKey, setIsCheckingAuth]);

  // Use React Query hooks for user details
  const {
    data: userDetails,
    isLoading: userLoading,
    isInitialLoading: userInitialLoading,
    isError: userError,
    error: userQueryError,
  } = useUserDetails(
    !!accessKey,
    (data) => {
      setUser(data);
      setAuthFailed(false);
      setIsCheckingAuth(false);
      if (!data || data.error || !data.is_verified) {
        setAuthFailed(true);
        setIsCheckingAuth(false);
        setAccessKey('');
      } else {
        // User details fetched successfully, enable identity fetch
        setShouldFetchIdentity(true);
      }
    },
    () => {
      setAuthFailed(true);
      setIsCheckingAuth(false);
      setAccessKey('');
    }
  );

  // Fetch user identity after user details are successfully fetched
  const { data: userIdentity, isLoading: identityLoading } = useUserIdentity(
    !!accessKey && shouldFetchIdentity,
    (error) => {
      console.error('Failed to fetch user identity:', error);
    }
  );

  // Check profile status
  const {
    data: profileData,
    isLoading: profileLoading,
    isError: profileError,
  } = useCheckProfile(
    !!accessKey,
    (data) => {
      // Check if profile exists and has status 'created'
      if (data?.error || !data || data.status !== 'created') {
        setOnboardingStep('not_started');
        setFirstTimeLogin(true);
      } else if (data.status === 'created') {
        setOnboardingStep('completed');
        // Mark as not first time login since profile exists
        if (firstTimeLogin) {
          setFirstTimeLogin(false);
        }
      }
    },
    (error) => {
      setOnboardingStep('not_started');
      setFirstTimeLogin(true);
    }
  );

  // Handle email changes and onboarding store logic
  useEffect(() => {
    if (!userIdentity?.email) return;

    const currentEmail = userIdentity.email;

    // Check if email has changed
    if (userEmail !== currentEmail) {
      // Email changed - check profile status
      if (!profileData || profileData.error || profileData.status !== 'created') {
        // Profile not created - reset onboarding store with new email
        resetOnboarding();
        setUserEmail(currentEmail);
        setFirstTimeLogin(true);
      } else {
        // Profile exists - just update email, keep firstTimeLogin as false
        setUserEmail(currentEmail);
        setFirstTimeLogin(false);
      }
    }
  }, [userIdentity, userEmail, profileData, setUserEmail, setFirstTimeLogin, resetOnboarding]);

  useEffect(() => {
    if (!accessKey) return;
    if (userError || userQueryError) {
      setAuthFailed(true);
      setIsCheckingAuth(false);
      setAccessKey('');
    } else if (!userLoading && !identityLoading && !profileLoading && userDetails && userIdentity) {
      setIsCheckingAuth(false);
    }
  }, [
    accessKey,
    userError,
    userQueryError,
    userLoading,
    identityLoading,
    profileLoading,
    userDetails,
    userIdentity,
    setIsCheckingAuth,
    setAccessKey,
  ]); // Show loading while checking authentication, identity, and profile
  if ((isCheckingAuth || userLoading || identityLoading || profileLoading) && !authFailed) {
    return (
      <div className='min-h-screen bg-gray-50 flex items-center justify-center'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4'></div>
          <p className='text-gray-600'>Verifying access...</p>
        </div>
      </div>
    );
  }

  // Show NoAuth page if authentication failed
  if (authFailed || !isAuthenticated) {
    return <NoAuthPage />;
  }

  // Redirect to onboarding if profile doesn't exist or has errors
  // Don't redirect if already on onboarding page
  if (onboardingStep === 'not_started' && !location.pathname.includes('/onboarding')) {
    return <Navigate to='/onboarding' replace />;
  }

  // Render protected content if authenticated and profile exists
  return children;
};

export default ProtectedRoute;
